import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Tippy from "@tippyjs/react/headless";
import { useContext, useDeferredValue, useState } from "react";
import styled, { css } from "styled-components";
import { boxShadow100, boxShadow200 } from "../../common/boxShadows";
import { zIndexLayers } from "../../common/zIndexLayers";
import { UISettingsProvider } from "../../core/context/UISettingsProvider";
import { cssBody200 } from "../typography/styles";
const arrowSize = 10;
export const getTooltipOffset = (v = 0, arrow = arrowSize) => [0, v + arrow / 2];
const offset = getTooltipOffset(4);
const TriangleIcon = (props) => (_jsxs("svg", { width: "10", height: "10", viewBox: "0 0 10 10", fill: "none", xmlns: "http://www.w3.org/2000/svg", ...props, children: [_jsx("path", { "data-triangle": "", d: "M9 0L1 -6.99382e-07L5 6.5L9 0Z" }), _jsx("path", { "data-border": "", d: "M4.15261 6.53L0.118405 -8.53632e-07L1.29385 -7.50871e-07L5.00061 6L8.70737 -1.02761e-07L9.88281 0L5.84861 6.53C5.45694 7.15667 4.54428 7.15667 4.15261 6.53Z" })] }));
/** @deprecated use `<Tooltip />` instead*/
export const NewTooltip = ({ colorType = "dark", content, children, maxWidth = 290, hideArrow, appendTo, immediatelyMount = false, small, ...props }) => {
    const [show, setShow] = useState(props.visible || false);
    const [mount, setMount] = useState(false);
    const contextSettings = useContext(UISettingsProvider);
    const deferredChildren = useDeferredValue(children);
    const deferredContent = useDeferredValue(content);
    return (_jsx("div", { onMouseEnter: () => setMount(true), style: { display: "contents" }, children: !mount && !props.singleton && !immediatelyMount ? (deferredChildren) : (_jsx(Tippy, { delay: [500, 500], zIndex: zIndexLayers.tooltip, offset: offset, ...contextSettings, ...props, appendTo: appendTo || contextSettings?.tooltips?.appendTo || document.body, animation: true, onMount: () => setShow(true), onHide: () => setShow(false), render: (attrs, _, instance) => {
                return (_jsxs(Content, { ...attrs, "$small": small, "$show": show, "$maxWidth": maxWidth, "$colorType": colorType, onTransitionEnd: () => {
                        if (!show) {
                            instance?.unmount?.();
                        }
                    }, children: [deferredContent, !hideArrow && (_jsx(Arrow, { "data-popper-arrow": "", children: _jsx(Triangle, {}) }))] }));
            }, children: deferredChildren })) }));
};
const Arrow = styled.div `
  width: ${arrowSize}px !important;
  height: ${arrowSize}px !important;
  text-align: initial;
`;
const Triangle = styled(TriangleIcon) `
  width: ${arrowSize}px !important;
  height: ${arrowSize}px !important;
  position: absolute;
`;
const themes = {
    dark: css `
    padding: ${(p) => p.theme.spacing.sp8};
    background-color: ${(p) => p.theme.systemColors.grey800};
    border-radius: 6px;

    color: ${(p) => p.theme.systemColors.white};

    ${Triangle} {
      path[data-triangle=""],
      path[data-border=""] {
        fill: ${(p) => p.theme.systemColors.grey800} !important;
      }
    }
  `,
    light: css `
    ${boxShadow100};
    padding: ${(p) => p.theme.spacing.sp16};
    background-color: ${(p) => p.theme.systemColors.white};
    border-radius: 6px;
    border: solid 1px ${(p) => p.theme.systemColors.grey300};
    color: ${(p) => p.theme.systemColors.grey600};

    ${Triangle} {
      path[data-triangle=""] {
        fill: ${(p) => p.theme.systemColors.white} !important;
      }

      path[data-border=""] {
        fill: ${(p) => p.theme.systemColors.grey300} !important;
      }
    }
  `,
    grey: css `
    ${boxShadow100};
    padding: ${(p) => p.theme.spacing.sp16};
    background-color: ${(p) => p.theme.systemColors.grey100};
    border-radius: 6px;
    border: none;
    color: ${(p) => p.theme.systemColors.grey600};
    ${boxShadow200};

    ${Triangle} {
      path[data-triangle=""] {
        fill: ${(p) => p.theme.systemColors.grey100} !important;
      }
    }
  `,
};
const Content = styled.div `
  max-width: ${({ $maxWidth }) => $maxWidth && typeof $maxWidth === "number" ? $maxWidth + "px" : $maxWidth || "auto"};
  transition: opacity 0.2s ease;

  ${cssBody200};

  ${(p) => themes[p.$colorType || "dark"]};

  ${(p) => p.$small && `padding: ${p.theme.spacing.sp8}`};

  ${(p) => p.$show
    ? css `
          opacity: 1;
        `
    : css `
          opacity: 0;
        `}
  &[data-placement^="top"] {
    ${Arrow} {
      bottom: 0;
    }

    ${Triangle} {
      top: ${arrowSize - 1}px;
    }
  }

  &[data-placement^="bottom"] {
    ${Arrow} {
      top: 0;
    }

    ${Triangle} {
      bottom: ${arrowSize - 1}px;
      transform: rotate(180deg);
    }
  }

  &[data-placement^="left"] {
    ${Arrow} {
      right: 0;
    }

    ${Triangle} {
      transform: rotate(-90deg);
      left: ${arrowSize - 1}px;
    }
  }

  &[data-placement^="right"] {
    ${Arrow} {
      left: 0;
    }

    ${Triangle} {
      transform: rotate(90deg);
      right: ${arrowSize - 1}px;
    }
  }
`;
